import { Component, OnInit, ViewChild } from '@angular/core';
import { BaseComponent, SpinnerComponent } from '@lib/components';
import { AngularMaterialModule } from 'src/app/material.module';
import { AccountType, ApiResponseError, IAccount, ICustomer, ISearchTransactionResponse, ITransaction, ITransactionFilter, ITransactionIndex, IWallet } from '@lib/interfaces';
import { MatSnackBar } from '@angular/material/snack-bar';
import { FilterMatcher, FilterOperator, Permission, TransactionExtendedType } from '@lib/enums';
import { ValueFieldModule } from '@lib/modules';
import { FormBuilder } from '@angular/forms';
import { AccountsService, TransactionService, SidePanelService, AuthService } from '@lib/services';
import { finalize, map } from 'rxjs';
import { OptionalPlaceholderPipe, TransactionTypePipe } from '@lib/pipes';
import { Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { MatDialogModule } from '@angular/material/dialog';
import { Utils } from '@lib/utils';
import { LoadFundsComponent } from '../load-funds/load-funds.component';
import { PayBillComponent } from '../pay-bill/pay-bill.component';
import { MaskStringPipe } from '@lib/pipes/mask-string/mask-string.pipe';
import { ChartConfiguration, ChartOptions, ChartType } from 'chart.js';
import { BaseChartDirective, NgChartsModule } from 'ng2-charts';
import { CdkTableModule } from '@angular/cdk/table';
import { CardLessService } from '@lib/services/card-less/card-less.service';
import { CardStatus, CardType, FulfillmentStatus, ICard, RiyadhCardType } from '@lib/interfaces/card.interface';
import { ChangeCardStatusModalComponent } from '@pages/cards-listing/components/change-card-status-modal/change-card-status-modal.component';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { IframeModalComponent } from '../iframe-modal/iframe-modal.component';

export enum Status {
    Approved = 'approved',
    Declined = 'declined',
    Settled = 'settled',
}

export interface ITransactionStatus {
    approved: ITransaction;
    declined: ITransaction;
    settled: ITransaction;
    value: string;
}
@Component({
    selector: 'app-riyad-home',
    templateUrl: './riyad-home.component.html',
    styleUrls: ['./riyad-home.component.scss'],
    standalone: true,
    imports: [AngularMaterialModule, SpinnerComponent, ValueFieldModule, TransactionTypePipe, MatDialogModule, MaskStringPipe, OptionalPlaceholderPipe, NgChartsModule, CdkTableModule],
})
export class RiyadHomeComponent extends BaseComponent implements OnInit {
    @ViewChild(BaseChartDirective) canvas!: BaseChartDirective;
    accounts: IAccount[] = [];
    customer: ICustomer;
    customerId: string;
    agentId: string;
    parentUserId: string;
    isSubAgent: string;
    masterAccountId: string;
    masterAccount: IAccount[];
    isLoading = true;
    walletForm = this.createWalletForm();
    protected cardType: RiyadhCardType;
    selectedWallet = { currency: this.walletForm.controls.currency.value, balance: 0 };

    protected wallets: { currency: string; balance: number; selected: boolean }[] = [];
    protected transactions: ITransactionIndex[] = [];
    protected transactionLoading = false;
    protected overviewLoading = true;

    card: ICard;
    protected cardStatus: typeof CardStatus = CardStatus;

    iframeUrl: SafeResourceUrl | null = null;
    transactionExtendedType = TransactionExtendedType;

    protected currentCardStatus: CardStatus;
    protected hasCardStatusChangePermission = this.utils.getPermissions(Permission.CardChangeStauts);

    constructor(
        private readonly accountsService: AccountsService,
        private readonly snackbar: MatSnackBar,
        protected readonly formBuilder: FormBuilder,
        private readonly dialog: MatDialog,
        private readonly transactionService: TransactionService,
        private readonly cardsLessService: CardLessService,
        private readonly router: Router,
        private utils: Utils,
        private authService: AuthService,
        private sanitizer: DomSanitizer
    ) {
        super();
        this.cardType = localStorage.getItem('cardType') as RiyadhCardType;
    }

    ngOnInit() {
        this.isSubAgent = localStorage.getItem('isSubAgent')!;
        this.customerId = localStorage.getItem('id')!;
        this.parentUserId = localStorage.getItem('parentUserId')!;

        this.filters;
        this.fetchCustomerAccounts();
        this.addSubscription(
            this.accountsService.reloadWalletList.subscribe({
                next: (value: any) => {
                    if (value) {
                        this.fetchCustomerAccounts();
                    }
                },
            }),
        );
        this.walletForm.controls.currency.valueChanges.subscribe((value) => {
            this.selectedWallet.currency = value;
        });
    }

    fetchCustomerAccounts() {
        this.accountsService
            .getAccountsByUserId(this.isSubAgent ? this.parentUserId : this.customerId, this.isSubAgent ? AccountType.MASTER : undefined)
            .pipe(
                finalize((): void => {
                    this.isLoading = false;
                }),
            )
            .subscribe({
                next: ({ data: accounts }) => {
                    if (accounts.length === 0) {
                        this.isLoading = false;
                        this.transactionLoading = false;
                        return;
                    }

                    const uniqueBalances = new Map();

                    accounts.forEach((account: IAccount) => {
                        const currency = Object.keys(account.balances)[0];
                        const balance = account.balances[currency];
                        uniqueBalances.set(currency, balance);
                    });

                    this.wallets = Array.from(uniqueBalances, ([currency, balance]) => ({
                        currency,
                        balance,
                        selected: currency === this.walletForm.controls.currency.value,
                    }));

                    this.wallets.map((wallet) => {
                        if (wallet.currency === this.walletForm.controls.currency.value) {
                            this.walletForm.controls.balance.setValue(wallet.balance);
                        }
                    });

                    this.selectedWallet = this.wallets.find((wallet: any) => wallet.currency === this.walletForm.controls.currency.value) || { currency: this.walletForm.controls.currency.value, balance: 0, selected: true };

                    this.accounts = accounts;
                    this.fecthCardDetails();
                    this.isLoading = false;
                },
                error: (error: ApiResponseError) => {
                    this.isLoading = false;
                    this.createApiErrorSnackBar(this.snackbar, error);
                },
            });
    }
    protected get filters(): Array<ITransactionFilter> {
        return [
            {
                field: 'status_code',
                operator: FilterOperator.ALL_OF,
                values: ['0000'],
            },
            {
                field: 'user_id',
                operator: FilterOperator.ALL_OF,
                values: [localStorage.getItem('id') || ''],
            },
        ];
    }

    protected createWalletForm() {
        return this.formBuilder.group({
            currency: this.formBuilder.nonNullable.control('SAR'),
            total: this.formBuilder.nonNullable.control(10000),
            balance: this.formBuilder.nonNullable.control(0),
        });
    }

    getInitials(names: string): string {
        const namesArray = names.split(' ');
        const initials = namesArray.map((name) => name.charAt(0).toUpperCase()).join('');
        return initials;
    }

    protected searchTransactions(): void {
        this.addSubscription(
            this.transactionService
                .searchTransactions({ limit: 10, filters: this.filters, matcher: FilterMatcher.AND })
                .pipe(
                    finalize((): void => {
                        this.transactionLoading = false;
                    }),
                )
                .subscribe({
                    next: ({ data }: ISearchTransactionResponse): void => {
                        this.transactions = this.filterInitialLoadTransaction(data);
                    },
                    error: (error: ApiResponseError): void => {
                        this.transactionLoading = false;
                        this.createApiErrorSnackBar(this.snackbar, error);
                    },
                }),
        );
    }

    protected fecthCardDetails(): void {
        const payload = {
            limit: '10',
            after: '',
            user_id: localStorage.getItem('id') || '',
        };
        const params: URLSearchParams = new URLSearchParams(payload);

        this.addSubscription(
            this.cardsLessService.fetchCardsLazyLoaded(params).subscribe({
                next: ({ data, after, has_more }: any) => {
                    this.card = data[0];
                    this.currentCardStatus = this.card.status as CardStatus;
                    if (this.currentCardStatus !== CardStatus.INACTIVE) {
                        this.searchTransactions();
                    }
                },
            }),
        );
    }

    formatDate(date: string) {
        if (date.length === 6) {
            return `${new Date(date.substring(0, 2)).toLocaleString('en-US', { month: 'short' })} ${date.substring(2, date.length)}`;
        }
        if (date.length === 8) {
            const currentDate = `${date.substring(2, 4)}/${date.substring(0, 2)}/${date.substring(4, date.length)}`;
            const formattedDate = new Date(currentDate).toLocaleString('en-US', {
                day: 'numeric',
                month: 'short',
                year: 'numeric',
            });
            return formattedDate;
        }
        throw new Error('Invalid date format');
    }

    protected redirectToWalletTransfer() {
        this.router.navigate(['/load-funds']);
    }

    protected async goToTransactionDetail(transaction: ITransactionIndex): Promise<void> {
        const canViewDetails: boolean = this.utils.getPermissions(Permission.TransactionDetails);

        if (!canViewDetails) {
            this.snackbar.open('You do not have permission to view transaction details', 'Close', { duration: 6000 });
            return;
        }
        await this.router.navigate(['transaction', transaction.id]);
    }

    protected openLoadFundsModal() {
        const dialog = this.dialog.open(LoadFundsComponent, {
            width: '450px',
            height: '100%',
            position: { right: '0px', top: '0px' },
            panelClass: 'dialog-side-panel',
            autoFocus: false,
            data: {
                accountId: this.accounts.find((account) => account.currencies.includes(this.selectedWallet.currency))?.id,
                initialLoad: this.accounts.find((account) => account.currencies.includes(this.selectedWallet.currency))?.wallet_details.find((wallet) => wallet.currency === this.selectedWallet.currency)?.allowed_load_type === 'INITIAL_LOAD',
            },
        });

        dialog.afterClosed().subscribe((data: boolean) => {
            if (data) {
                setTimeout(() => {
                    this.fetchCustomerAccounts();
                }, 1000);
            }
        });
    }

    protected openPayBillModal() {
        const dialog = this.dialog.open(PayBillComponent, {
            width: '450px',
            height: '100%',
            position: { right: '0px', top: '0px' },
            panelClass: 'dialog-side-panel',
            autoFocus: false,
            data: {
                accountId: this.accounts.find((account) => account.currencies.includes(this.selectedWallet.currency))?.id,
                balance: this.selectedWallet.balance,
            },
        });

        dialog.afterClosed().subscribe((data: boolean) => {
            if (data) {
                setTimeout(() => {
                    this.fetchCustomerAccounts();
                }, 1000);
            }
        });
    }

    protected getAvailablePercent(): number {
        return (this.selectedWallet.balance / this.walletForm.controls.total.value) * 100;
    }

    protected isCardActive(): boolean {
        return this.currentCardStatus === CardStatus.ACTIVE;
    }

    getTransactionType(transaction: string): string {
        if (this.cardType === RiyadhCardType.PREPAID) {
            if (transaction === TransactionExtendedType.WALLET_CREDIT) {
                return 'Card Top-up';
            }
            return transaction;
        } else {
            if (transaction === TransactionExtendedType.WALLET_CREDIT) {
                return 'Payment Received, Thank you';
            }
            return transaction
        }
        return '';
    }

    private filterInitialLoadTransaction(data: ITransactionIndex[]) {
        if (this.cardType === RiyadhCardType.PREPAID) {
            return data;
        }
        return data.filter((transaction) => transaction.transaction_type !== TransactionExtendedType.INITIAL_LOAD);
    }

    openCardStatusChange(card: ICard) {
        if (!this.hasCardStatusChangePermission) {
            this.snackbar.open('You do not have permission to change card status', 'Close', { duration: 6000 });
            return;
        }

        const dialogRef = this.dialog.open(ChangeCardStatusModalComponent, {
            autoFocus: false,
            width: '550px',
            data: card,
        });

        dialogRef.afterClosed().subscribe((result) => {
            if (result) {
                card.status = result.status;
                card.modified_by = result.modified_by;
                card.modified = result.modified;
                this.currentCardStatus = result.status;
            }
        });
    }

    protected showCardDetails() {
        this.authService.createLUT(this.card.id).subscribe({
            next: (response: any) => {
                const data = {
                    "card_id": this.card.id,
                    "lut": response.token,
                    "source_id": "default_pci_source"
                };
                const base64String = btoa(JSON.stringify(data));
                const url = `https://showcardwidget.stg.platform.ae-1.nymcard.com/reveal-card-details/${base64String}`;
                const iframeUrl = this.sanitizer.bypassSecurityTrustResourceUrl(url);

                // Open the iframe in a modal
                this.dialog.open(IframeModalComponent, {
                    data: { iframeUrl },
                    width: '35%',
                    height: '35%',
                    
                    panelClass: 'iframe-modal-container'
                });
            },
        });
    }

    protected isSent(type: TransactionExtendedType): boolean {
        const sentTypes = [TransactionExtendedType.PURCHASE, TransactionExtendedType.CASH_WITHDRAWAL, TransactionExtendedType.WALLET_DEBIT, TransactionExtendedType.WALLET_TRANSFER];
        return sentTypes.includes(type);
    }
}
